import { useCallback, useEffect, useRef } from "react";
import { useAuth } from "@/hooks/useAuth";
import { useRecordProgressEvent } from "@/hooks/useProgress";
import { normalizeModeId } from "@/lib/study-modes";
import type { ProgressEventPayload, ProgressEventType } from "@/lib/api-types";

/**
 * Standard tracker every viewer page calls on mount. Fires:
 *   - `chapter_opened`  once per (chapter, session)
 *   - `mode_started`    once per (chapter, mode, session)
 *   - `time_heartbeat`  every 30s while the tab is visible AND the
 *                       viewer is the active route
 *   - `mode_completed`  once per (chapter, mode, session), when the page calls
 *                       `reportEndReached()` AND the engagement floor is met
 *
 * Anonymous users skip everything (the backend authorizer would 401
 * anyway). The hook is intentionally fire-and-forget -- failures are
 * silenced so a transient progress-handler error doesn't surface in
 * the viewer UI.
 *
 * Dedup is via session-scoped Sets in `sessionStorage` so a chapter
 * the user revisits within one tab session doesn't double-count.
 */

const SESSION_KEY = "staxed:viewer-tracker:fired";
const HEARTBEAT_INTERVAL_MS = 30_000;
/** How often we re-check whether the completion conditions are now both met. */
const COMPLETION_POLL_MS = 5_000;

type ModeId =
  | "textbook" | "notes" | "question-bank" | "slides"
  | "podcast"  | "video"
  | "flashcards" | "mind-map" | "quiz";

export type ViewerTracker = {
  /**
   * Call when the viewer's content has been consumed to its end -- last page,
   * last card, last question, media played through.
   *
   * Reaching the end is necessary but NOT sufficient: `mode_completed` only
   * fires once `minDwellSeconds` of visible, active time has also accrued. A
   * student who flicks the scrollbar to the bottom in two seconds has not
   * finished a chapter, and this number is what a paying parent reads. Safe to
   * call repeatedly -- the event still fires at most once per session.
   */
  reportEndReached: () => void;
};

export function useViewerTracker(opts: {
  chapterId: string | undefined;
  mode: ModeId;
  /** Defaults to true. Set to false while the viewer is in a paywall or
   *  loading state, so we don't count time the user can't actually read. */
  active?: boolean;
  /**
   * Visible-tab seconds required before `reportEndReached()` can complete the
   * mode. 0 means the end signal is trusted on its own -- correct for the media
   * viewers, where "90% actually played" already carries the time proof.
   */
  minDwellSeconds?: number;
}): ViewerTracker {
  const { chapterId, mode, active = true, minDwellSeconds = 0 } = opts;
  const { status } = useAuth();
  const record = useRecordProgressEvent();
  // The handler's VALID_MODES is underscored; sending the UI's hyphenated ids made
  // `mode_started` 400 for question-bank and mind-map.
  const wireMode = normalizeModeId(mode);

  // Stable function reference -- avoids re-effecting on every render.
  const recordRef = useRef(record.mutate);
  recordRef.current = record.mutate;

  // ---------- chapter_opened + mode_started: once per session
  useEffect(() => {
    if (status !== "authenticated" || !chapterId || !active) return;
    fireOnce(`chapter_opened::${chapterId}`, () => {
      recordRef.current({ event_type: "chapter_opened", chapter_id: chapterId } as ProgressEventPayload);
    });
    fireOnce(`mode_started::${chapterId}::${wireMode}`, () => {
      recordRef.current({ event_type: "mode_started", chapter_id: chapterId, mode: wireMode } as ProgressEventPayload);
    });
  }, [chapterId, wireMode, active, status]);

  // ---------- dwell clock: visible + active seconds only
  // Accumulated from timestamps rather than a per-second tick, so a backgrounded
  // tab accrues nothing and we don't run a timer just to count.
  const dwellMsRef = useRef(0);
  const visibleSinceRef = useRef<number | null>(null);
  const endReachedRef = useRef(false);

  const dwellMs = useCallback(
    () => dwellMsRef.current + (visibleSinceRef.current == null ? 0 : Date.now() - visibleSinceRef.current),
    [],
  );

  const maybeComplete = useCallback(() => {
    if (status !== "authenticated" || !chapterId) return;
    if (!endReachedRef.current) return;
    if (dwellMs() < minDwellSeconds * 1000) return;
    fireOnce(`mode_completed::${chapterId}::${wireMode}`, () => {
      recordRef.current({
        event_type: "mode_completed",
        chapter_id: chapterId,
        mode: wireMode,
      } as ProgressEventPayload);
    });
  }, [chapterId, wireMode, status, minDwellSeconds, dwellMs]);

  const reportEndReached = useCallback(() => {
    endReachedRef.current = true;
    maybeComplete();
  }, [maybeComplete]);

  // ---------- time_heartbeat every 30s while visible + active
  useEffect(() => {
    if (status !== "authenticated" || !chapterId || !active) return;

    let heartbeat: number | null = null;
    let completionPoll: number | null = null;

    const start = () => {
      if (visibleSinceRef.current == null) visibleSinceRef.current = Date.now();
      if (heartbeat == null) {
        heartbeat = window.setInterval(() => {
          if (document.visibilityState !== "visible") return;
          recordRef.current({
            event_type: "time_heartbeat",
            chapter_id: chapterId,
            mode: wireMode,
            seconds: HEARTBEAT_INTERVAL_MS / 1000,
          } as ProgressEventPayload);
        }, HEARTBEAT_INTERVAL_MS);
      }
      // The student may hit the end of the content before the dwell floor is met;
      // this is what completes the mode when the clock catches up.
      if (completionPoll == null && minDwellSeconds > 0) {
        completionPoll = window.setInterval(maybeComplete, COMPLETION_POLL_MS);
      }
    };

    const stop = () => {
      if (visibleSinceRef.current != null) {
        dwellMsRef.current += Date.now() - visibleSinceRef.current;
        visibleSinceRef.current = null;
      }
      if (heartbeat != null) { window.clearInterval(heartbeat); heartbeat = null; }
      if (completionPoll != null) { window.clearInterval(completionPoll); completionPoll = null; }
    };

    const onVisibility = () => {
      if (document.visibilityState === "visible") start();
      else stop();
    };
    document.addEventListener("visibilitychange", onVisibility);
    if (document.visibilityState === "visible") start();

    return () => {
      stop();
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [chapterId, wireMode, active, status, minDwellSeconds, maybeComplete]);

  return { reportEndReached };
}

// --------------------------------------------------------------------------
function fireOnce(key: string, fn: () => void): void {
  if (typeof window === "undefined") return;
  try {
    const raw = sessionStorage.getItem(SESSION_KEY);
    const set = raw ? new Set<string>(JSON.parse(raw) as string[]) : new Set<string>();
    if (set.has(key)) return;
    set.add(key);
    sessionStorage.setItem(SESSION_KEY, JSON.stringify(Array.from(set)));
    fn();
  } catch {
    // sessionStorage disabled (private mode etc.) -- still fire once per
    // mount, just no dedup across remounts.
    fn();
  }
}

/**
 * Fire-and-forget helper for one-off events that aren't part of the
 * baseline tracker -- quiz_attempted, mode_completed, mode_progress.
 * Used by individual viewer pages at the right moment (e.g. quiz end).
 */
export function useFireProgressEvent(): (event_type: ProgressEventType, partial: Omit<ProgressEventPayload, "event_type">) => void {
  const { status } = useAuth();
  const record = useRecordProgressEvent();
  const fn = (event_type: ProgressEventType, partial: Omit<ProgressEventPayload, "event_type">) => {
    if (status !== "authenticated") return;
    record.mutate({ event_type, ...partial } as ProgressEventPayload);
  };
  return fn;
}
